@if (isOpen()) {
  <!-- Backdrop -->
  <div
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-in fade-in"
    (click)="close()"
    (keydown.escape)="close()"
  >
    <!-- Dialog panel -->
    <dialog
      class="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg bg-white shadow-xl animate-in zoom-in-95 fade-in duration-200 open:block"
      open
      (click)="$event.stopPropagation()"
      (keydown)="$event.stopPropagation()"
      [attr.aria-labelledby]="titleId"
    >
      @if (isDetailLoading()) {
        <!-- Loading state: close button + centred spinner -->
        <div class="flex flex-col items-center justify-center py-24 px-6">
          <button
            (click)="close()"
            class="absolute top-4 right-4 p-2 rounded-md hover:bg-gray-100 transition-colors text-muted"
            aria-label="Close dialog"
          >
            <lucide-angular name="x" [size]="18" />
          </button>
          <app-loading-spinner size="md" />
        </div>
      } @else {

      <!-- Header -->
      <div class="p-6 pb-4 border-border">
        <!-- Top row: flag + jurisdiction + badges + action buttons -->
        <div class="flex items-start justify-between gap-3 mb-2">
          <div class="flex items-center gap-2 flex-wrap">
            <app-jurisdiction-flag [jurisdiction]="update().jurisdiction" />
            <span class="text-sm font-medium text-gray-700">{{ update().jurisdiction }}</span>
            <span
              [class]="impactBadgeClass()"
              class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize"
            >
              {{ update().impactLevel }} Impact
            </span>
            @if (update().restriction) {
              <span class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                {{ update().restriction }}
              </span>
            }
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <button
              (click)="toggleFavourite.emit(update().id)"
              class="p-2 rounded-md hover:bg-gray-100 transition-colors"
              [attr.aria-label]="update().isFavourited ? 'Remove from favourites' : 'Add to favourites'"
            >
              <lucide-angular
                name="star"
                [size]="18"
                [class]="update().isFavourited ? 'text-yellow-500 fill-yellow-500' : 'text-muted'"
              />
            </button>
            <button
              (click)="close()"
              class="p-2 rounded-md hover:bg-gray-100 transition-colors text-muted"
              aria-label="Close dialog"
            >
              <lucide-angular name="x" [size]="18" />
            </button>
          </div>
        </div>

        <!-- Title -->
        <h2 [id]="titleId" class="text-xl font-bold text-gray-900 leading-snug mb-2">{{ update().title }}</h2>

        <!-- Metadata row: published · updated · category -->
        <div class="flex items-center gap-3 flex-wrap text-xs text-muted mb-4">
          <span class="flex items-center gap-1">
            <lucide-angular name="calendar" [size]="12" />
            Published {{ pubDate() }}
          </span>
          @if (updatedDate()) {
            <span class="flex items-center gap-1">
              <lucide-angular name="refresh-cw" [size]="12" />
              Updated {{ updatedDate() }}
            </span>
          }
          @if (ingestedDate()) {
            <span class="text-gray-400">Ingested {{ ingestedDate() }}</span>
          }
        </div>

        <!-- Document Type + Publisher Rank row -->
        @if (update().categoryName || update().rank) {
          <div class="flex items-center justify-between flex-wrap border-t border-border pt-3 mb-2">
            @if (update().categoryName) {
              <div class="flex items-center gap-2">
                <span class="text-sm font-semibold text-gray-700">Category:</span>
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-teal-50 text-teal-700 border-teal-200">
                  {{ update().categoryName }}
                </span>
              </div>
            }
            @if (update().rank) {
              <div class="flex items-center gap-1.5 text-sm">
                <span class="font-semibold text-gray-700">Publisher Rank:</span>
                <span class="text-gray-800 font-medium">{{ update().rank }}</span>
                <span class="text-muted">(lower = more authoritative)</span>
              </div>
            }
          </div>
        }

        <!-- Publisher row -->
        @if (update().source) {
          <div class="flex items-center gap-1.5 text-sm text-muted mb-2">
            <lucide-angular name="building-2" [size]="13" class="shrink-0" />
            <span class="font-semibold text-gray-700">Publisher:</span>
            <span>{{ update().source }}</span>
          </div>
        }

        <!-- View Source button -->
        @if (update().sourceUrl) {
          <a
            [href]="update().sourceUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1.5 text-sm border border-gray-200 rounded-md px-3 py-1.5 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <lucide-angular name="external-link" [size]="13" />
            View Source
          </a>
        }

      </div>

      <!-- Body -->
      <div class="px-6 pb-6 space-y-4">

        <!-- Summary -->
        <div class="border-b border-border pb-6">
          <markdown
            [data]="update().content"
            class="prose prose-sm max-w-none text-gray-800 prose-headings:text-base prose-headings:font-semibold prose-headings:mb-1 prose-headings:mt-3 prose-p:text-sm prose-p:my-1 prose-li:text-sm prose-li:my-0 prose-ul:my-1 prose-ol:my-1"
          />
        </div>

        <!-- Topics / Sign Posts -->
        @if (hasEvidence()) {
          <div>
            <div class="flex items-center gap-2 mb-4">
              <lucide-angular name="file-text" [size]="15" class="text-muted" />
              <h3 class="text-sm font-semibold text-gray-700">Topics</h3>
            </div>
            <div class="space-y-4">
              @for (group of pageGroups(); track group.page) {
                <div  class="border-b border-border pb-4">
                  <!-- Tag pills for this page -->
                  <div class="flex flex-wrap gap-1.5 mb-3">
                    @for (post of group.posts; track post.id) {
                      <span class="inline-flex items-center rounded px-2 py-0.5 text-xs font-mono bg-gray-100 text-gray-700 border border-gray-200">
                        {{ post.tag }}
                      </span>
                    }
                  </div>
                  <!-- Inference text for each post -->
                  @for (post of group.posts; track post.id) {
                    <p class="text-sm text-gray-600 italic leading-relaxed mb-3 whitespace-pre-wrap">" {{ post.inferenceText }} "</p>
                  }
                  <!-- Page badge -->
                  <a
                    [href]="update().sourceUrl + '#page=' + (group.page)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-1.5 rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 transition-colors"
                  >
                    <lucide-angular name="file-text" [size]="12" />
                    Page {{ group.page }}
                  </a>
                </div>
              }
            </div>
          </div>
        }

        <!-- Copyright Notice -->
        @if (update().copyRightNotice) {
          <div class="flex items-start gap-1.5 text-xs text-muted">
            <lucide-angular name="shield" [size]="13" class="mt-0.5 shrink-0" />
            <span>{{ update().copyRightNotice }}</span>
          </div>
        }

      </div>

      } <!-- end @else (isDetailLoading) -->
    </dialog>
  </div>
}
